import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, inject } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PlannerViewsStateService } from '../../../../services/planner-views-state.service';
import { CustomRouteService } from 'src/app/pages/custom-route/services/custom-route.service';
import { MatDialogService } from 'src/app/services/mat-dialog.service';
import { DialogComponent } from 'src/app/shared/dialogs/dialog/dialog.component';
import { EditRouteService } from '../../services/edit-route.service';
import { HttpErrorResponse } from '@angular/common/http';
import { updateCustomEditedRouteBodyAdapter } from '../../adapters/update-custom-edited-route-body.adapter';
import { PlannerDayStateService } from '../../../../services';
import { CustomEditedRouteResponse } from '../../interfaces/edit-route.response';
import { CustomRouteResponseCompleted } from 'src/app/pages/custom-route/interfaces/custom-route.response';
import { openCiSnackbar } from '@iurban-malaga/cicerone-ui';
import { DialogSuccessErrorEditRouteResponseComponent } from 'src/app/shared/dialogs/dialog-success-error-edit-route-response/dialog-success-error-edit-route-response.component';

@Component({
  selector: 'app-action-buttons',
  standalone: true,
  imports: [
    TranslateModule
  ],
  templateUrl: './action-buttons.component.html',
  styleUrls: ['./action-buttons.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ActionButtonsComponent {
  private _translateService = inject(TranslateService);
  private _customRouteService = inject(CustomRouteService);
  private _editRouteService = inject(EditRouteService);
  private _plannerViewsStateService = inject(PlannerViewsStateService);
  private _plannerDayStateService = inject(PlannerDayStateService);
  private _matDialogService = inject(MatDialogService);
  
  private readonly _token = computed(() => this._customRouteService.token());
  private readonly _customRouteData = computed(() => this._customRouteService.customRouteData());
  private readonly _customRouteEditedDataToRender = computed(() => this._editRouteService.customRouteEditedDataToRender());
  private readonly _initialStateCustomRouteEditedDataToRender = computed(() => this._editRouteService.initialStateCustomRouteEditedDataToRender());
  private readonly _customRouteEditedDataToSend = computed(() => this._editRouteService.customRouteEditedDataToSend());
  private readonly _dayToShowIndex = computed(() => this._plannerDayStateService.dayToShowIndex());
  
  public disabledButton: boolean = false;
  
  updateButtonStateAccordingToChanges = effect(() => {
    const initialState = this._initialStateCustomRouteEditedDataToRender();
    const currentState = this._customRouteEditedDataToRender();
    
    if (!initialState || !currentState) {
      this.disabledButton = true; 
      return;
    } 

    this.disabledButton = JSON.stringify(initialState) === JSON.stringify(currentState);
  }, { allowSignalWrites: true });  
  
  public handleCancelEditClick() {
    this._openCloseConfirmationDialog();
  }
  
  public handleUpdateEditClick() {
    this._sendNewEditedCustomRouteData();
  }
  
  private _sendNewEditedCustomRouteData() {
    this._customRouteService.setIsLoading(true);
    
    const customRouteData = this._customRouteData();
    if (!customRouteData) return;
    
    const editedCustomRouteDataToSend = this._customRouteEditedDataToSend();
    console.log('Edited custom route data To Send:', editedCustomRouteDataToSend);
    
    const editedCustomEditedRouteBody = updateCustomEditedRouteBodyAdapter(editedCustomRouteDataToSend, this._token(), this._dayToShowIndex());
    
    this._customRouteService.updateCustomRouteData(editedCustomEditedRouteBody)
    .subscribe({
      next: (response) => {
        this._customRouteService.setIsLoading(false);
        console.log('Custom route data updated successfully:', response);
        this._customRouteService.updateDailyRoute(response as CustomRouteResponseCompleted, this._dayToShowIndex());
        this._redirectToPlannerView();

        // handle successes/errors in the response UX 
        if (response.error && response.error.length > 0) {
          console.log('Successes updating custom route data:', response.success);
          console.error('Errors updating custom route data:', response.error);

          this._openSuccessErrorListUpdateCustomRouteDialog(response);
        } else {
          this._openSuccessUpdateCustomRouteSnackbar();
        }
        
        this._redirectToPlannerView();
      },
      error: (error: HttpErrorResponse) => {
        this._customRouteService.setIsLoading(false);
        this._openServerErrorUpdateCustomRouteDialog();
        console.error(`Error updating custom route data: ${error}`);
      }
    });
  }
  
  private _openServerErrorUpdateCustomRouteDialog(): void {
    const dialogRef = this._matDialogService.openMatDialog({
      component: DialogComponent,
      data: {
        iconName: 'alert',
        title: this._translateService.instant('Error en el servidor'),
        content: this._translateService.instant('Ha ocurrido un error') + '. ' + this._translateService.instant('Inténtelo de nuevo más tarde'),
        primaryActionButtonText: this._translateService.instant('Aceptar'),
        handleClickPrimaryButton: () => {
          this._matDialogService.closeMatDialog(dialogRef);
        }
      }
    });
  }
  
  private _openSuccessErrorListUpdateCustomRouteDialog(response: CustomEditedRouteResponse): void {
    const dialogRef = this._matDialogService.openMatDialog({
      component: DialogSuccessErrorEditRouteResponseComponent,
      data: {
        iconName: 'notification-active',
        title: this._translateService.instant('Ruta modificada'),
        content: { errorList: response.error, successList: response.success },
        primaryActionButtonText: this._translateService.instant('Entendido'),
        handleClickPrimaryButton: () => {
          this._matDialogService.closeMatDialog(dialogRef);
        }
      }
    });
  }
  
  private _openCloseConfirmationDialog(): void {
    const dialogRef = this._matDialogService.openMatDialog({
      component: DialogComponent,
      data: {
        iconName: 'alert',
        title: '¿' + this._translateService.instant('Quieres regresar') + '?',
        content: this._translateService.instant('Si vuelves atrás se borraran todos los cambios que se han realizado durante la edición de tu ruta'),
        primaryActionButtonText: this._translateService.instant('Aceptar'),
        secondaryActionButtonText: this._translateService.instant('Cancelar'),
        handleClickPrimaryButton: () => {
          this._redirectToPlannerView();
          this._matDialogService.closeMatDialog(dialogRef);
        },
        handleClickSecondaryButton: () => {
          this._matDialogService.closeMatDialog(dialogRef);
        }
      }
    })
  }
  
  private _openSuccessUpdateCustomRouteSnackbar() {
    openCiSnackbar({
      content: this._translateService.instant('Ruta actualizada correctamente'),
      status: 'success',
      'icon-name': 'btn-check',
      'horizontal-position': 'end',
      'vertical-position': 'bottom',
      duration: 4000
    })
  } 
  
  private _redirectToPlannerView() {
    this._plannerViewsStateService.isEditRouteView.set(false);
  }
}
